import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ResizableImage from '../components/ResizableImageExtension';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { DOMParser as PMMDOMParser } from '@tiptap/pm/model';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { ySyncPlugin, yCursorPlugin, yUndoPlugin } from 'y-prosemirror';
import api from '../utils/api';
import dayjs from 'dayjs';
import {
  ArrowLeft, Trash2, Send, MessageCircle, Reply,
  CornerDownRight, WifiOff, X,
} from 'lucide-react';
import FileAttachments from '../components/FileAttachments';
import EditorToolbar from '../components/EditorToolbar';
import MarkdownPaste from '../extensions/MarkdownPaste';
import { markdownToHtml } from '../utils/markdownUtils';
import { getContrastColor } from '../utils/color';
import { useTheme } from '../contexts/ThemeContext';

const COLORS = ['#f87171','#fb923c','#facc15','#4ade80','#22d3ee','#818cf8','#e879f9','#f472b6'];

function getUserColor(id) {
  return COLORS[id % COLORS.length];
}

// ── 링크 자동 감지 ──
function Linkify({ children }) {
  if (!children) return null;
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const parts = String(children).split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-brand hover:underline break-all">{part}</a>
      : part
  );
}

// ── 이미지 업로드 ──
async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await api.post('/boards/upload', fd);
  return r.data.url;
}

// ── 댓글 아이템 ──
function CommentItem({ c, onReply, onDelete, replyTo, setReplyTo }) {
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submitReply = async () => {
    if (!replyText.trim() || submitting) return;
    setSubmitting(true);
    await onReply(c.id, replyText);
    setReplyText('');
    setSubmitting(false);
    setReplyTo(null);
  };

  return (
    <div>
      <div className="bg-gray-800/50 rounded-lg px-3 py-2 group">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-white text-xs font-medium">{c.author?.name || c.author?.username}</span>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 text-xs">{dayjs(c.createdAt).format('MM.DD HH:mm')}</span>
            <button onClick={() => setReplyTo(replyTo === c.id ? null : c.id)}
              className="text-gray-600 hover:text-brand opacity-0 group-hover:opacity-100"><Reply size={12} /></button>
            <button onClick={() => onDelete(c.id)}
              className="text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100"><Trash2 size={12} /></button>
          </div>
        </div>
        <p className="text-gray-300 text-sm whitespace-pre-wrap"><Linkify>{c.content}</Linkify></p>
      </div>
      {c.replies?.length > 0 && (
        <div className="ml-5 mt-1 space-y-1">
          {c.replies.map(r => (
            <div key={r.id} className="bg-gray-800/30 rounded-lg px-3 py-2 group flex gap-2">
              <CornerDownRight size={12} className="text-gray-600 mt-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-white text-xs font-medium">{r.author?.name || r.author?.username}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 text-xs">{dayjs(r.createdAt).format('MM.DD HH:mm')}</span>
                    <button onClick={() => onDelete(r.id)}
                      className="text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100"><Trash2 size={12} /></button>
                  </div>
                </div>
                <p className="text-gray-300 text-sm whitespace-pre-wrap"><Linkify>{r.content}</Linkify></p>
              </div>
            </div>
          ))}
        </div>
      )}
      {replyTo === c.id && (
        <div className="ml-5 mt-1 flex gap-2">
          <CornerDownRight size={14} className="text-gray-600 mt-2.5 flex-shrink-0" />
          <input value={replyText} onChange={e => setReplyText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submitReply()}
            placeholder="답글 입력..." autoFocus
            className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-brand" />
          <button onClick={submitReply} disabled={submitting}
            className="px-2 py-1.5 bg-brand text-white rounded-lg hover:bg-brand/80 disabled:opacity-50"><Send size={12} /></button>
        </div>
      )}
    </div>
  );
}

// ── 메인 ──
export default function TaskEditorPage({ basePath = '/boards' }) {
  const { theme } = useTheme();
  const avatarColor = (color) => theme === 'light' ? getContrastColor(color) : 'white';
  const { boardId, taskId } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('mfg_user') || '{}');

  const [task, setTask] = useState(null);
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const fileInputRef = useRef(null);
  const mdFileInputRef = useRef(null);
  const titleRef = useRef(null);

  // Yjs 문서 + Provider 동기 생성 (useEditor보다 먼저 존재해야 함)
  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc();
    const token = localStorage.getItem('mfg_token');
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const isLocal = ['localhost', '127.0.0.1', '100.116.73.97'].includes(window.location.hostname);
    const wsHost = isLocal ? `${window.location.hostname}:3800` : window.location.host;
    const prov = new WebsocketProvider(`${wsProtocol}//${wsHost}/yjs`, `task-${taskId}`, doc, {
      params: { token },
    });
    return { ydoc: doc, provider: prov };
  }, [taskId]);

  // 제목 Y.Text
  const ytitle = useMemo(() => ydoc.getText('title'), [ydoc]);

  // 태스크 정보 로드
  const loadTask = useCallback(() => {
    api.get(`/boards/${boardId}/tasks/${taskId}`).then(r => {
      setTask(r.data);
      setComments(r.data.comments || []);
    });
  }, [boardId, taskId]);

  useEffect(() => { loadTask(); }, [loadTask]);

  // Provider 이벤트 바인딩 + cleanup
  useEffect(() => {
    provider.on('status', ({ status }) => {
      setConnected(status === 'connected');
    });
    if (provider.wsconnected) setConnected(true);

    provider.awareness.setLocalStateField('user', {
      name: user.name || user.username,
      color: getUserColor(user.id || 0),
    });

    const awarenessHandler = () => {
      const seen = new Set();
      const users = [];
      provider.awareness.getStates().forEach((state) => {
        if (state.user && !seen.has(state.user.name)) {
          seen.add(state.user.name);
          users.push({ ...state.user, isMe: false });
        }
      });
      // 본인이 목록에 없으면 추가
      const myName = user.name || user.username;
      if (!seen.has(myName)) {
        users.unshift({ name: myName, color: getUserColor(user.id || 0), isMe: true });
      } else {
        const idx = users.findIndex(u => u.name === myName);
        if (idx >= 0) { users[idx].isMe = true; if (idx > 0) { users.unshift(users.splice(idx, 1)[0]); } }
      }
      setPeers(users);
    };
    provider.awareness.on('change', awarenessHandler);
    awarenessHandler(); // 초기값

    return () => {
      provider.destroy();
      ydoc.destroy();
    };
  }, [provider, ydoc]);

  // 제목 동기화 + DB 디바운스 저장
  const [title, setTitle] = useState('');
  const [saveStatus, setSaveStatus] = useState(null);
  const titleSaveTimer = useRef(null);
  const saveStatusTimer = useRef(null);

  useEffect(() => {
    const update = () => {
      const val = ytitle.toString();
      setTitle(val);
      // 제목 변경 시 DB에 디바운스 저장 (1초)
      if (titleSaveTimer.current) clearTimeout(titleSaveTimer.current);
      titleSaveTimer.current = setTimeout(async () => {
        if (!val) return;
        setSaveStatus('saving');
        try {
          await api.put(`/boards/${boardId}/tasks/${taskId}`, { title: val });
          setSaveStatus('saved');
          clearTimeout(saveStatusTimer.current);
          saveStatusTimer.current = setTimeout(() => setSaveStatus(null), 2000);
        } catch { setSaveStatus('error'); }
      }, 1000);
    };
    ytitle.observe(update);

    // provider synced 이벤트로 초기값 세팅
    const onSync = (isSynced) => { if (isSynced) setTitle(ytitle.toString()); };
    provider.on('synced', onSync);
    if (provider.synced) setTitle(ytitle.toString());

    return () => {
      ytitle.unobserve(update);
      provider.off('synced', onSync);
      if (titleSaveTimer.current) clearTimeout(titleSaveTimer.current);
    };
  }, [ytitle, provider, boardId, taskId]);

  const handleTitleChange = (e) => {
    const newVal = e.target.value;
    ydoc.transact(() => {
      ytitle.delete(0, ytitle.length);
      if (newVal) ytitle.insert(0, newVal);
    });
  };

  // y-prosemirror 플러그인을 Tiptap 확장으로 래핑
  const YjsExtension = useMemo(() => Extension.create({
    name: 'yjs',
    addProseMirrorPlugins() {
      const fragment = ydoc.getXmlFragment('content');
      return [
        ySyncPlugin(fragment),
        yCursorPlugin(provider.awareness, {
          cursorBuilder: (user) => {
            const cursor = document.createElement('span');
            cursor.className = 'yjs-cursor';
            cursor.style.borderColor = user.color;
            const label = document.createElement('span');
            label.className = 'yjs-cursor-label';
            label.style.backgroundColor = user.color;
            label.textContent = user.name;
            cursor.appendChild(label);
            return cursor;
          },
        }),
        yUndoPlugin(),
      ];
    },
  }), [ydoc, provider]);

  // Tiptap 에디터
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ history: false }),
      YjsExtension,
      ResizableImage,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder: '내용을 입력하세요...' }),
      MarkdownPaste,
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    autofocus: false,
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[400px] px-6 py-4',
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (files?.length) {
          event.preventDefault();
          Array.from(files).forEach(async (f) => {
            if (!f.type.startsWith('image/')) return;
            const url = await uploadImage(f);
            view.dispatch(view.state.tr.replaceSelectionWith(
              view.state.schema.nodes.image.create({ src: url })
            ));
          });
          return true;
        }
        return false;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (!file) continue;
            (async () => {
              const url = await uploadImage(file);
              view.dispatch(view.state.tr.replaceSelectionWith(
                view.state.schema.nodes.image.create({ src: url })
              ));
            })();
            return true;
          }
        }
        return false;
      },
    },
  }, [ydoc]);

  // 이미지 업로드 버튼
  const handleImageButton = () => fileInputRef.current?.click();
  const handleMdUpload = () => mdFileInputRef.current?.click();
  const handleMdFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    const text = await file.text();
    const html = markdownToHtml(text);
    const view = editor.view;
    const parser = PMMDOMParser.fromSchema(view.state.schema);
    const dom = document.createElement('div');
    dom.innerHTML = html;
    const slice = parser.parseSlice(dom, { preserveWhitespace: false });
    const tr = view.state.tr.replaceSelection(slice);
    view.dispatch(tr.scrollIntoView());
    e.target.value = '';
  };
  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files?.length || !editor) return;
    for (const f of files) {
      if (!f.type.startsWith('image/')) continue;
      const url = await uploadImage(f);
      editor.chain().focus().setImage({ src: url }).run();
    }
    e.target.value = '';
  };

  // 댓글
  const addComment = async (parentId = null, text = null) => {
    const content = text || comment;
    if (!content.trim() || submitting) return;
    setSubmitting(true);
    await api.post(`/boards/${boardId}/tasks/${taskId}/comments`, { content, parentId });
    if (!parentId) setComment('');
    setSubmitting(false);
    loadTask();
  };
  const deleteComment = async (cid) => {
    await api.delete(`/boards/${boardId}/tasks/${taskId}/comments/${cid}`);
    loadTask();
  };

  // 삭제
  const handleDelete = async () => {
    if (!confirm('이 할일을 삭제하시겠습니까?')) return;
    await api.delete(`/boards/${boardId}/tasks/${taskId}`);
    navigate(basePath);
  };

  const totalComments = comments.reduce((sum, c) => sum + 1 + (c.replies?.length || 0), 0);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-gray-950 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button onClick={() => navigate(basePath)} className="p-1.5 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800 flex-shrink-0">
            <ArrowLeft size={18} />
          </button>
          <input ref={titleRef} value={title} onChange={handleTitleChange}
            placeholder="제목을 입력하세요"
            className="flex-1 bg-transparent text-white text-lg font-bold focus:outline-none placeholder-gray-600 min-w-0" />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* 접속자 */}
          <div className="flex items-center gap-1 mr-2">
            <div className="flex -space-x-1.5">
              {peers.slice(0, 5).map((p, i) => (
                <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${p.isMe ? 'border-brand' : 'border-gray-950'}`}
                  style={{ backgroundColor: p.color, color: avatarColor(p.color) }} title={p.isMe ? `${p.name} (나)` : p.name}>
                  {p.name?.charAt(0)}
                </div>
              ))}
              {peers.length > 5 && <span className="text-gray-500 text-xs ml-1">+{peers.length - 5}</span>}
            </div>
          </div>
          {saveStatus === 'saving' && <span className="text-gray-500 text-xs">저장 중...</span>}
          {saveStatus === 'saved' && <span className="text-green-500 text-xs">저장됨</span>}
          {saveStatus === 'error' && <span className="text-red-400 text-xs">저장 실패</span>}
          {/* 연결 상태 — 끊겼을 때만 표시 */}
          {!connected && (
            <div className="flex items-center gap-1 text-red-400 text-xs" title="연결 끊김">
              <WifiOff size={14} /> 오프라인
            </div>
          )}
          {/* 댓글 토글 */}
          <button onClick={() => setShowComments(!showComments)}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm ${showComments ? 'bg-brand/20 text-brand' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}>
            <MessageCircle size={14} />
            {totalComments > 0 && <span className="text-xs">{totalComments}</span>}
          </button>
          {/* 삭제 */}
          {(task?.authorId === user.id || user.role === 'superadmin') && (
            <button onClick={handleDelete} className="p-1.5 text-gray-500 hover:text-red-400 rounded-lg hover:bg-gray-800">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {/* 메타 정보 */}
      {task && (
        <div className="px-6 py-2 text-xs text-gray-500 border-b border-gray-800/50 flex-shrink-0">
          {task.author?.name || task.author?.username} · {dayjs(task.createdAt).format('YYYY.MM.DD HH:mm')}
          {task.completed && task.completer && <span className="text-green-400/70"> · {task.completer.name} 완료</span>}
        </div>
      )}

      {/* 본문 영역 */}
      <div className="flex flex-1 min-h-0">
        {/* 에디터 */}
        <div className="flex-1 flex flex-col min-w-0">
          <EditorToolbar editor={editor} onImageUpload={handleImageButton} onMarkdownUpload={handleMdUpload} />
          <div className="flex-1 overflow-y-auto">
            <EditorContent editor={editor} />
          </div>
          <FileAttachments entityType="task" entityId={task?.id} editor={editor} />
          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
          <input ref={mdFileInputRef} type="file" accept=".md,text/markdown" className="hidden" onChange={handleMdFile} />
        </div>

        {/* 댓글 패널 */}
        {showComments && (
          <div className="w-80 border-l border-gray-800 flex flex-col bg-gray-950 flex-shrink-0">
            <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-white text-sm font-semibold flex items-center gap-1.5">
                <MessageCircle size={14} /> 댓글
                {totalComments > 0 && <span className="text-gray-500">({totalComments})</span>}
              </h3>
              <button onClick={() => setShowComments(false)} className="text-gray-500 hover:text-white"><X size={14} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {comments.map(c => (
                <CommentItem key={c.id} c={c} onReply={addComment} onDelete={deleteComment}
                  replyTo={replyTo} setReplyTo={setReplyTo} />
              ))}
              {comments.length === 0 && <p className="text-gray-600 text-xs text-center py-4">댓글이 없습니다.</p>}
            </div>
            <div className="p-3 border-t border-gray-800 flex gap-2">
              <input value={comment} onChange={e => setComment(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !submitting && addComment()}
                placeholder="댓글 입력..."
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-brand" />
              <button onClick={() => addComment()} disabled={submitting}
                className="px-2 py-1.5 bg-brand text-white rounded-lg hover:bg-brand/80 disabled:opacity-50">
                <Send size={12} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
